/* eslint-disable @typescript-eslint/member-ordering */
import {
  AfterViewInit,
  ComponentFactoryResolver,
  ComponentRef,
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewContainerRef
} from '@angular/core';
import { Platform } from '@ionic/angular';

import { TooltipBoxComponent } from '../components/atomic/tooltip/tooltip-box.component';
// import { TooltipService } from '../services/tooltip.service';

@Directive({
  selector: '[tooltip]'
})
export class TooltipDirective implements OnInit, AfterViewInit, OnDestroy {
  @Input() tooltipHtml: string;
  @Input() tooltip: string;
  @Input() positionV: string;
  @Input() positionH: string;
  @Input() event: 'press' | 'click' | 'hover';
  @Input() mobileEvent: 'press' | 'click' = 'press';
  @Input() desktopEvent: 'press' | 'click' | 'hover' = 'hover';
  @Input() topOffset: number;
  @Input() leftOffset: number;
  @Input() hideOthers: boolean;

  @Input() duration = 3000;

  @Input()
  set active(val: boolean) {
    this._active = val;
    if (this._active) {
      this.showTooltip();
    } else {
      this.removeTooltip();
    }
  }

  private tooltipBoxElement: ComponentRef<TooltipBoxComponent>;
  private tooltipTimeout: any;
  private _active = true;

  constructor(
    private el: ElementRef,
    private platform: Platform,
    private cfr: ComponentFactoryResolver,
    // private tooltipService: TooltipService,
    private vcr: ViewContainerRef
  ) {}

  get active(): boolean {
    return this._active;
  }

  @HostListener('click')
  onClick(): void {
    if (this.event === 'click') {
      this.trigger();
    }
  }

  @HostListener('press')
  onPress(): void {
    if (this.event === 'press') {
      this.trigger();
    }
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (this.event === 'hover') {
      this.active = true;
    }
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    if (this.event === 'hover') {
      this.active = false;
    }
  }

  ngOnInit(): void {
    if (!this.event) {
      this.event = this.platform.is('mobile') ? this.mobileEvent : this.desktopEvent;
    }
  }

  ngAfterViewInit(): void {
    if (this.active) {
      this.trigger();
    }
  }

  ngOnDestroy(): void {
    this.removeTooltip();
  }

  trigger(): void {
    if (this.tooltipBoxElement) {
      this.resetTimer();
    } else {
      this.showTooltip();
    }
  }

  showTooltip(): void {
    this.createTooltipComponent();

    const tooltipComponent: TooltipBoxComponent = this.tooltipBoxElement.instance;

    tooltipComponent.tooltipText = this.tooltip;
    tooltipComponent.tooltipHtml = this.tooltipHtml;
    setTimeout(() => {
      const tooltipPosition = this.getTooltipPosition();
      tooltipComponent.posLeft = tooltipPosition.left;
      tooltipComponent.posTop = tooltipPosition.top;

      tooltipComponent.fadeState = 'visible';

      if (!this.active) {
        this.tooltipTimeout = setTimeout(this.removeTooltip.bind(this), this.duration);
      }
    });
  }

  removeTooltip(): void {
    if (!this.tooltipBoxElement) {
      this.tooltipBoxElement = undefined;
      this.tooltipTimeout = undefined;
      return;
    }

    this.tooltipBoxElement.instance.fadeState = 'invisible';

    // wait for animation to finish then clear everything out
    setTimeout(() => {
      if (this.tooltipBoxElement && typeof this.tooltipBoxElement.destroy === 'function') {
        this.tooltipBoxElement.destroy();
      }
      // this.tooltipService.removeTooltip(this);
      this.tooltipBoxElement = this.tooltipTimeout = undefined;
    }, 300);
  }

  private createTooltipComponent(): void {
    this.tooltipBoxElement = this.vcr.createComponent(TooltipBoxComponent);
    // this.tooltipService.addTooltip(this);
  }

  private getTooltipPosition(): { left: number; top: number } {
    const tooltipNativeElement: HTMLElement = this.tooltipBoxElement.instance.getNativeElement();
    const el: HTMLElement = this.el.nativeElement;
    const rect: DOMRect = this.getBoundingRect(el);

    let positionLeft: number;
    let positionTop: number;
    const spacing = 10;

    if (this.positionH === 'right') {
      positionLeft = rect.right + spacing;
    } else if (this.positionH === 'left') {
      positionLeft = rect.left - spacing - tooltipNativeElement.offsetWidth;
    } else {
      positionLeft = rect.left;
    }

    if (this.positionV === 'top') {
      positionTop = rect.top - spacing - tooltipNativeElement.offsetHeight;
    } else if (this.positionV === 'bottom') {
      positionTop = rect.bottom + spacing;
    } else {
      positionTop = rect.top + el.offsetHeight / 2 - tooltipNativeElement.offsetHeight / 2;
    }

    if (+this.topOffset) {
      positionTop += +this.topOffset;
    }
    if (+this.leftOffset) {
      positionLeft += +this.leftOffset;
    }

    if (positionLeft + tooltipNativeElement.offsetWidth + spacing > this.platform.width()) {
      positionLeft = this.platform.width() - tooltipNativeElement.offsetWidth - spacing;
    } else if (positionLeft + tooltipNativeElement.offsetWidth - spacing < 0) {
      positionLeft = spacing;
    }

    if (positionTop + tooltipNativeElement.offsetHeight + spacing > this.platform.height()) {
      positionTop = this.platform.height() - tooltipNativeElement.offsetHeight - spacing;
    } else if (positionTop + tooltipNativeElement.offsetHeight - spacing < 0) {
      positionTop = spacing;
    }

    return {
      left: positionLeft,
      top: positionTop
    };
  }

  private resetTimer(): void {
    clearTimeout(this.tooltipTimeout);
    this.tooltipTimeout = setTimeout(() => {
      this.active = false;
    }, this.duration);
  }

  private getBoundingRect(element): DOMRect {
    const style = window.getComputedStyle(element);
    const margin = {
      left: parseInt(style['margin-left'], 10),
      right: parseInt(style['margin-right'], 10),
      top: parseInt(style['margin-top'], 10),
      bottom: parseInt(style['margin-bottom'], 10)
    };
    const padding = {
      left: parseInt(style['padding-left'], 10),
      right: parseInt(style['padding-right'], 10),
      top: parseInt(style['padding-top'], 10),
      bottom: parseInt(style['padding-bottom'], 10)
    };
    const border = {
      left: parseInt(style['border-left'], 10),
      right: parseInt(style['border-right'], 10),
      top: parseInt(style['border-top'], 10),
      bottom: parseInt(style['border-bottom'], 10)
    };

    let rect = element.getBoundingClientRect();
    rect = {
      left: rect.left - margin.left,
      right: rect.right - margin.right - padding.left - padding.right,
      top: rect.top - margin.top,
      bottom: rect.bottom - margin.bottom - padding.top - padding.bottom - border.bottom
    };
    rect.width = rect.right - rect.left;
    rect.height = rect.bottom - rect.top;
    return rect;
  }
}
